import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Stack,
  Typography,
  IconButton,
  Button,
  TextField,
  Paper,
  Alert,
} from '@mui/material';
import { X, ChevronDown, ChevronUp } from 'lucide-react';
import { NameEmailAvatar } from '../../../../../../../ui';
import {
  addPanelistNotes,
  getInterviewApplicant,
  getInterviewRound,
} from '../../../../../../../utils/api_service/SmartInterview';

export interface IPanelistNotes {
  open: boolean;
  handleClosePanelistNotes: () => void;
  intervieweeName?: string;
  email?: string;
  interviewRoundId: string;
  applicantId: string;
  onSuccess?: () => void;
}

export const PanelistNotesDialog: React.FC<IPanelistNotes> = ({
  open,
  handleClosePanelistNotes,
  email,
  intervieweeName,
  interviewRoundId,
  applicantId,
  onSuccess,
}) => {
  const [newNote, setNewNote] = useState<string>('');
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editingNoteText, setEditingNoteText] = useState<string>('');
  const [isPanelist, setIsPanelist] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existingNotes, setExistingNotes] = useState<
    Array<{
      id: number;
      createdAt: string;
      panelist: string;
      notes: string;
    }>
  >([]);
  const [panelistsMap, setPanelistsMap] = useState<Record<string, any>>({});
  const [userPanelistId, setUserPanelistId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const userId = localStorage.getItem('id') || '';

  // Fetch interview round panelists and check if current user is a Panelist
  useEffect(() => {
    if (!interviewRoundId) return;

    const fetchRoundInfo = async () => {
      try {
        const response = await getInterviewRound(interviewRoundId);
        if (!response.ok) throw new Error(`Status ${response.status}`);
        const round = await response.json();

        const panelistsRecord: Record<string, any> = {};
        let foundPanelistId: string | null = null;

        const panelists = round?.panelists || [];
        panelists.forEach((p: any) => {
          if (p?.user?.id) {
            panelistsRecord[p.id] = {
              firstName: p.user.firstName,
              lastName: p.user.lastName,
              email: p.user.email,
              avatar: p.user.profilePic || '',
            };

            if (p.user.id === userId) {
              foundPanelistId = p.id;
            }
          }
        });

        setPanelistsMap(panelistsRecord);
        setUserPanelistId(foundPanelistId);
        setIsPanelist(foundPanelistId !== null);
      } catch (err) {
        setPanelistsMap({});
        setUserPanelistId(null);
        setIsPanelist(false);
      }
    };

    fetchRoundInfo();
  }, [interviewRoundId, userId]);

  // Fetch existing Panelist notes for the applicant
  useEffect(() => {
    if (!open || !interviewRoundId || !applicantId) return;

    const fetchNotes = async () => {
      try {
        const res = await getInterviewApplicant(applicantId, interviewRoundId);
        const result = await res.json();

        let notes = [];
        if (result?.PenalistNotes) {
          notes = result.PenalistNotes;
        } else if (result?.panelistNotes) {
          notes = result.panelistNotes;
        }

        setExistingNotes(notes);

        const userNote = notes.find(
          (note: { id: number; panelist: string; notes: string }) =>
            (note.panelist || '').trim() === userPanelistId,
        );

        if (userNote) {
          setEditingNoteId(userNote.id);
          setEditingNoteText(userNote.notes);
          setExpanded(false);
        } else {
          setExpanded(true);
          setEditingNoteId(null);
          setEditingNoteText('');
        }
      } catch (err) {
        setExistingNotes([]);
        setExpanded(true);
      }
    };

    fetchNotes();
  }, [open, applicantId, interviewRoundId, userPanelistId]);

  const handleSave = async () => {
    if (!isPanelist || loading) {
      return;
    }

    const isNewNote = editingNoteId === null;
    const noteToSave = isNewNote ? newNote : editingNoteText;

    if (!noteToSave.trim()) {
      setError('Please enter some notes before saving');
      return;
    }

    if (!isNewNote) {
      const originalNote = existingNotes.find(
        (note) => note.id === editingNoteId,
      );
      if (originalNote && originalNote.notes.trim() === noteToSave.trim()) {
        setEditingNoteId(null);
        setEditingNoteText('');
        setExpanded(false);
        handleClosePanelistNotes();
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      const newNoteObj = {
        id: isNewNote ? Date.now() : editingNoteId,
        notes: noteToSave,
        panelist: userPanelistId || '',
        createdAt: isNewNote
          ? new Date().toISOString()
          : existingNotes.find((n) => n.id === editingNoteId)?.createdAt ||
            new Date().toISOString(),
      };

      if (isNewNote) {
        setExistingNotes((prev) => [newNoteObj, ...prev]);
      } else {
        setExistingNotes((prev) =>
          prev.map((note) =>
            note.id === editingNoteId ? { ...note, notes: noteToSave } : note,
          ),
        );
      }

      await addPanelistNotes(interviewRoundId, applicantId, noteToSave);
      setNewNote('');
      setEditingNoteId(null);
      setEditingNoteText('');
      setExpanded(false);

      onSuccess?.();
      handleClosePanelistNotes();
    } catch (err) {
      console.error('Error saving notes:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to save notes. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClosePanelistNotes}
      maxWidth='md'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant='h6' sx={{ fontWeight: 600 }}>
          Panelist Notes
        </Typography>
        <IconButton
          onClick={handleClosePanelistNotes}
          size='small'
          sx={{ color: 'text.secondary' }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={3}>
          {/* Interviewee Info */}
          <Paper
            variant='outlined'
            sx={{
              p: 1.5,
              bgcolor: 'action.hover',
            }}
          >
            <NameEmailAvatar
              avatar=''
              email={email || 'Unknown Email'}
              name={intervieweeName || 'Unknown Candidate'}
            />
          </Paper>

          {/* Notes list */}
          <Stack spacing={2} sx={{ maxHeight: 400, overflowY: 'auto', pr: 0.5 }}>
            {existingNotes.map((note, index) => {
              const panelistId = (note.panelist || '').trim();
              const isCurrentUserNote =
                isPanelist && panelistId === userPanelistId;
              const isEditing =
                isCurrentUserNote && editingNoteId === note.id && expanded;

              const panelistInfo = panelistsMap[panelistId];
              const panelistName = panelistInfo
                ? `${panelistInfo.firstName || ''} ${
                    panelistInfo.lastName || ''
                  }`.trim()
                : 'Unknown Panelist';

              return (
                <Box key={index}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 1,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <NameEmailAvatar
                        avatar={panelistInfo?.avatar || ''}
                        avatarHeight='32px'
                        avatarWidth='32px'
                        email={panelistInfo?.email || ''}
                        name={panelistName}
                      />
                      {note.createdAt && (
                        <Typography
                          variant='caption'
                          sx={{
                            ml: 5,
                            mt: 0.5,
                            display: 'block',
                            color: 'text.secondary',
                          }}
                        >
                          {new Date(note.createdAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </Typography>
                      )}
                    </Box>

                    {isCurrentUserNote && !isEditing && (
                      <Button
                        size='small'
                        onClick={() => {
                          setEditingNoteId(note.id);
                          setEditingNoteText(note.notes);
                          setExpanded(true);
                        }}
                        sx={{ textTransform: 'none' }}
                      >
                        Edit
                      </Button>
                    )}
                  </Box>

                  {isEditing ? (
                    <Stack spacing={1.5} sx={{ mt: 1, px: 1 }}>
                      <TextField
                        multiline
                        rows={3}
                        fullWidth
                        value={editingNoteText}
                        onChange={(e) => setEditingNoteText(e.target.value)}
                        placeholder='Edit your note...'
                        disabled={loading}
                        size='small'
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                          variant='outlined'
                          size='small'
                          onClick={() => {
                            setEditingNoteId(null);
                            setEditingNoteText('');
                            setExpanded(false);
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant='contained'
                          size='small'
                          onClick={handleSave}
                          disabled={
                            loading ||
                            !editingNoteText.trim() ||
                            editingNoteText.trim() === note.notes.trim()
                          }
                        >
                          Save
                        </Button>
                      </Box>
                    </Stack>
                  ) : (
                    <Paper
                      variant='outlined'
                      sx={{
                        ml: 5,
                        mt: 1,
                        p: 1.5,
                        fontSize: '0.875rem',
                        bgcolor: 'action.hover',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {note.notes}
                    </Paper>
                  )}
                </Box>
              );
            })}

            {/* Add New Note Section */}
            {isPanelist &&
              !existingNotes.some(
                (note) => (note.panelist || '').trim() === userPanelistId,
              ) && (
                <>
                  {!expanded ? (
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant='outlined'
                        size='small'
                        startIcon={<ChevronDown size={18} />}
                        onClick={() => {
                          setEditingNoteId(null);
                          setExpanded(true);
                        }}
                      >
                        Add New Note
                      </Button>
                    </Box>
                  ) : (
                    <Stack spacing={1.5} sx={{ mt: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
                          New Note
                        </Typography>
                        <Button
                          size='small'
                          endIcon={<ChevronUp size={18} />}
                          onClick={() => setExpanded(false)}
                          sx={{ textTransform: 'none' }}
                        >
                          Hide
                        </Button>
                      </Box>
                      <TextField
                        multiline
                        rows={4}
                        fullWidth
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        placeholder='Write your feedback, impressions, or notes from the interview...'
                        disabled={loading}
                      />
                      {error && (
                        <Alert severity='error' sx={{ mt: 1 }}>
                          {error}
                        </Alert>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                          variant='outlined'
                          size='small'
                          onClick={() => setExpanded(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant='contained'
                          size='small'
                          onClick={handleSave}
                          disabled={loading || !newNote.trim()}
                        >
                          Save
                        </Button>
                      </Box>
                    </Stack>
                  )}
                </>
              )}

            {existingNotes.length === 0 && !isPanelist && (
              <Typography
                variant='body2'
                sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}
              >
                No notes available.
              </Typography>
            )}
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default PanelistNotesDialog;
